import OLMap from 'ol/Map.js';
import Feature from 'ol/Feature.js';
import {Vector as VectorLayer} from 'ol/layer.js';
import {Vector as VectorSource} from 'ol/source.js';
import {Circle as CircleStyle, Fill, Stroke, Style} from 'ol/style.js';
import {createStringXY} from 'ol/coordinate.js';

import AttributionControl from 'ol/control/Attribution.js';
import RotateControl from 'ol/control/Rotate.js';
import ZoomControl from 'ol/control/Zoom.js';
import MousePosition from 'ol/control/MousePosition.js';
import ScaleLine from 'ol/control/ScaleLine.js';

import olStyleCircle from 'ol/style/Circle.js';
import olStyleFill from 'ol/style/Fill.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olStyleStyle from 'ol/style/Style.js';

import LayerSwitcher from 'ol-layerswitcher';

import CenterViewControl from 'macommune/centerviewcontrol.js';
import ControlsGroup from 'macommune/controlsgroup.js';
import ExtentSelectionControl from 'macommune/extentselectioncontrol.js';
import LegendControl from 'macommune/legendcontrol.js';

import constants, {dynamicUrl} from 'api/constants.js';
import * as themes from 'api/Themes.js';

constants.backgroundLayer = 'OSM (basemaps)';

class Map extends OLMap {
  constructor(options) {
    constants.themesUrl = options.themesUrl;
    constants.localeUrl = options.localeUrl;

    const controlsGroup = new ControlsGroup();
    const extentSelectionControl = new ExtentSelectionControl({
      target: controlsGroup.element,
    });
    const legendControl = new LegendControl();

    options.controls = [
      controlsGroup,
      new ZoomControl({
        target: controlsGroup.element,
        zoomInTipLabel: 'Zoom +',
        zoomOutTipLabel: 'Zoom -',
      }),
      new CenterViewControl({
        target: controlsGroup.element,
      }),
      extentSelectionControl,
      new RotateControl({
        target: controlsGroup.element,
      }),

      new AttributionControl(),
      new ScaleLine(),
      new LayerSwitcher(),
      legendControl,
    ];

    super(options);

    this.extentSelectionControl = extentSelectionControl;
    this.legendControl = legendControl;
    this.currentThemeLayers = [];

    this.featureSource = new VectorSource();
    this.featureLayer = new VectorLayer({
      name: 'featureLayer',
      zIndex: 2,
      source: this.featureSource,
    });
    this.featureLayer.setStyle(
      new olStyleStyle({
        image: new olStyleCircle({
          fill: new olStyleFill({color: [255, 170, 0, 0.6]}),
          radius: 5,
          stroke: new olStyleStroke({color: [255, 170, 0, 1], width: 2}),
        }),
      })
    );
    this.addLayer(this.featureLayer);

    this.selectionSource = new VectorSource();
    this.selectionLayer = new VectorLayer({
      zIndex: 2,
      source: this.selectionSource,
    });
    this.selectionLayer.setStyle(
      new olStyleStyle({
        image: new olStyleCircle({
          fill: new olStyleFill({color: [255, 0, 0, 0.6]}),
          radius: 5,
          stroke: new olStyleStroke({color: [255, 0, 0, 1], width: 2}),
        }),
      })
    );
    this.addLayer(this.selectionLayer);

    this.communeSource = new VectorSource();
    this.communeLayer = new VectorLayer({
      zIndex: 1,
      source: this.communeSource,
    });
    this.communeLayer.setStyle(
      new Style({
        fill: new Fill({
          color: [0, 0, 0, 0],
        }),
        stroke: new Stroke({
          color: [255, 255, 0, 1],
          width: 5,
        }),
      })
    );
    this.addLayer(this.communeLayer);
  }

  getLocalePromise() {
    if (!constants.localeUrl) {
      // Fallback to an empty dict
      return Promise.resolve({});
    }
    if (!this.localePromise) {
      this.localePromise = fetch(constants.localeUrl)
        .then((response) => response.json())
        .then(
          (data) =>
            // Return the first property as data should looks like { 'fr': { ... } }
            data[Object.keys(data)[0]]
        );
    }
    return this.localePromise;
  }

  loadBackgrounds() {
    return Promise.all([this.getLocalePromise(), themes.getBackgroundLayers()]).then((results) => {
      const translations = results[0];
      const layers = results[1];
      for (const layer of layers) {
        layer.set('type', 'base');
        if (layer.get('config.name') === constants.backgroundLayer) {
          layer.set('visible', true);
        } else {
          layer.set('visible', false);
        }
        layer.set('title', translations[layer.get('config.name')] || layer.get('config.name'));
        this.addLayer(layer);
      }
    });
  }

  clearCommune() {
    this.communeSource.clear();
  }

  selectCommune(feature) {
    // Remove previous commune
    this.clearCommune();

    this.communeSource.addFeature(feature);
    if (this.getSize()[0] == 0) {
      this.once('change:size', () => this.fitCommune());
    } else {
      this.fitCommune();
    }
  }

  fitCommune() {
    this.getView().fit(this.communeSource.getExtent(), {
      constrainResolution: false,
      padding: [10, 10, 10, 10],
    });
  }

  clearTheme() {
    for (let layer of this.currentThemeLayers) {
      this.removeLayer(layer);
    }
    this.currentThemeLayers = [];
  }

  selectTheme(theme_def) {
    // Remove previous layers
    this.clearTheme();

    if (theme_def) {
      // ... then get overlay layers (if defined)
      var overlayLayerNames = theme_def.layers;
      if (overlayLayerNames && overlayLayerNames.length) {
        Promise.all([this.getLocalePromise(), themes.getOverlayLayers(overlayLayerNames)]).then((results) => {
          const translations = results[0];
          const layers = results[1];
          for (const layer of layers) {
            //layer.set('title', translations[layer.get('config.name')] || layer.get('config.name'));
            layer.set('title', undefined);
            this.addLayer(layer);
            this.currentThemeLayers.push(layer);
          }
          this.legendControl.setLayers(this.currentThemeLayers);
        });
      }
    }
  }
}

export default Map;
